import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {View, StyleSheet } from 'react-native';
import { COLORS } from '@bcm/constants';
import { useNavigation } from '@react-navigation/native';

import Image from '../../UI/image/Image';
import palette from '../utils/canvasPalette';
import thickness from '../utils/canvasThickness';
import { calcDimensionsToFillContainer } from '../../../src/imageUtils';
import IconButton from '../../UI/button/IconButton';
import IconMap from '../../UI/icon/IconMap';
import ImageCanvasMenu from './ImageCanvasMenu';
import CanvasDraw from '../utils/web/canvasUtils';
import commonStyles from '../../UI/styles/common';
import { ImageGetSize } from '../../UI/image/Image';
import navbar from '../../UI/styles/navbar';
import HeaderLeft from '../../UI/headerButton/HeaderLeft';
import Alert from '../../UI/modal/Alert';
import { FormText } from '../../../constants/Text';
import { ALERT_TYPES } from '../../../constants/UIConstants';
import useBackHandler from '../../UI/utils/useBackHandler';

const ImageCanvas = ({
	onClose,
	containerDims,
	image,
	sketch,
	save,
	isLandscape,
	setIsDirty,
	isNavBar
}) => {

	const canvasRef = useRef();
	const navigation = useNavigation();

	const [canvasData, setCanvasData] = useState();
	const [isLoading, setIsLoading] = useState(true);
	const [selectedImageHeight, setSelectedImageHeight] = useState();
	const [selectedImageWidth, setSelectedImageWidth] = useState();
	const [cancelAlertVisible, setCancelAlertVisible] = useState(false);
	const [isDirtyLocal, setIsDirtyLocal] = useState(false);

	const [brushColor, setBrushColor] = useState(palette[0].value);
	const [brushRadius, setBrushRadius] = useState(thickness[2].value);
	const [isTransparent, setIsTransparent] = useState(false);
	const [isEraser, setIsEraser] = useState(false);

	const clearCanvas = () => canvasRef.current.clear(true);
	const toggleEraser = () => setIsEraser(!isEraser);
	const selectThickness = (size) => setBrushRadius(size);
	const selectColor = useCallback((brushColor) => {
		setBrushColor(brushColor);
		setIsEraser(false);
	}, []);
	const undo = () => canvasRef.current.undo();
	const toggleTransparancy = useCallback(() => setIsTransparent(!isTransparent), [isTransparent]);

	const saveHandler = useCallback(async () => {
		const canvasData = canvasRef.current.getDataURL();
		await save(canvasData);
	}, [save]);

	const onRequestCloseHandler = useCallback(() => {
		if(isDirtyLocal) setCancelAlertVisible(true);
		else onClose();
	}, [onClose, isDirtyLocal]);

	useEffect(() => {
		if(image && containerDims && containerDims.height && containerDims.width) {
			ImageGetSize(image, (x, y) => {
				const multiplier = isLandscape ? 2.5 : 3.5;
				const res = calcDimensionsToFillContainer(
					containerDims.height - multiplier * navbar.height,
					containerDims.width, y, x
				);
				setSelectedImageHeight(res.maxHeight);
				setSelectedImageWidth(res.maxWidth);
			});
		}
	}, [image, containerDims, isLandscape]);

	useEffect(() => {
		if(image && sketch) setCanvasData(sketch);
		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		return () => {
			if (isNavBar) navigation.setOptions({ headerLeft: () => <HeaderLeft onPress={navigation.goBack}/> });
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(setIsDirty) setIsDirty(isDirtyLocal);
		if(isNavBar) navigation.setOptions({ headerLeft: () => <HeaderLeft onPress={onRequestCloseHandler}/> });
	}, [navigation, isDirtyLocal, setIsDirty, onRequestCloseHandler, isNavBar]);

	useBackHandler(() => {
		if (isDirtyLocal) onRequestCloseHandler();
		return isDirtyLocal ? true : false;
	});

	return (
		<View style={styles.container}>
			<Alert
				alertVisible={cancelAlertVisible}
				setAlertVisible={setCancelAlertVisible}
				title='Discard Body Site Canvas?'
				onSubmit={onClose}
				onSubmitText={FormText.proceed}
				onCancelText={FormText.back}
				alertType={ALERT_TYPES.confirmCancel}
			/>
			{!isLandscape && <View style={styles.navbar}>
				<IconButton
					onPress={onRequestCloseHandler}
					icon={IconMap.canvasBack}
					text={'Back'}
				/>
				<IconButton
					onPress={toggleTransparancy}
					icon={isTransparent ? IconMap.eye : IconMap.eyeOff}
					text={isTransparent ? 'Show' : 'Hide'}
				/>
				<IconButton
					onPress={saveHandler}
					icon={IconMap.canvasSave}
					text={'Save'}
				/>
			</View>}
			{!isLoading && image && <View style={commonStyles.containers.center}>
				<Image
					source={{uri: image}}
					style={{
						position: 'absolute',
						width: selectedImageWidth,
						height: selectedImageHeight,
					}}
				/>
				<View style={{ opacity: isTransparent ? 0 : 1}}>
					<CanvasDraw
						ref={canvasRef}
						brushColor={brushColor}
						brushRadius={brushRadius}
						lazyRadius={2}
						canvasWidth={selectedImageWidth}
						canvasHeight={selectedImageHeight}
						hideGrid
						canvasData={canvasData}
						erase={isEraser}
						backgroundColor={(0,0,0,0)}
						setIsDirty={setIsDirtyLocal}
					/>
				</View>
			</View>}
			<View style={styles.navbar}>
				<IconButton
					onPress={clearCanvas}
					icon={IconMap.delete}
					text={'Clear'}
				/>
				<ImageCanvasMenu
					onPress={selectThickness}
					items={thickness}
					icon={IconMap.resize}
					text='Resize'
				/>
				<ImageCanvasMenu
					onPress={selectColor}
					items={palette}
					icon={IconMap.palette}
					text='Color'
				/>
				<IconButton
					onPress={toggleEraser}
					icon={(isEraser) ? IconMap.brush : IconMap.eraser}
					text={(isEraser) ? 'Brush' : 'Eraser'}
				/>
				<IconButton
					onPress={undo}
					icon={IconMap.undo}
					text={'Undo'}
				/>
				{isLandscape && <IconButton
					onPress={toggleTransparancy}
					icon={isTransparent ? IconMap.eye : IconMap.eyeOff}
					text={isTransparent ? 'Show' : 'Hide'}
				/>}
				{isLandscape && <IconButton
					onPress={saveHandler}
					icon={IconMap.save}
					text={'Save'}
				/>}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: COLORS.white,
	},
	navbar: {
		height: 60,
		width: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-evenly',
	}
});

export default memo(ImageCanvas);